/**
 * The on-screen keyboard (SPEC §8.2). Safari has no interactive-widget / VirtualKeyboard API, so
 * we use visualViewport and expose the keyboard inset as the CSS variable --kb (and --kb-open =
 * 0|1). Little rides the keyboard (just Chat's box): pop-ups stay put while they fit above it (one
 * it would cover moves up just enough, Sheet.tsx) and toasts move to the top; a field the keyboard
 * would cover scrolls up into view instead.
 */
import { useSyncExternalStore } from 'react'

let kb = 0
let vvTop = -1
const subs = new Set<() => void>()

function update() {
  const vv = window.visualViewport
  if (!vv) return
  // Where the visible area starts (iOS can scroll it under the keyboard): centred pop-ups use it.
  const top = Math.max(0, Math.round(vv.offsetTop))
  if (top !== vvTop) {
    vvTop = top
    document.documentElement.style.setProperty('--vv-top', `${top}px`)
  }
  const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
  const next = inset > 80 ? inset : 0
  if (next !== kb) {
    kb = next
    document.documentElement.style.setProperty('--kb', `${kb}px`)
    document.documentElement.style.setProperty('--kb-open', kb ? '1' : '0')
    for (const f of subs) f()
    // Keep the focused field visible above the keyboard.
    if (kb) {
      const el = document.activeElement as HTMLElement | null
      if (el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable)) requestAnimationFrame(() => reveal(el))
    }
  }
}

/** The box that scrolls it (a pop-up's inside, a list), or the page. */
function scroller(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY
    if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight) return p
  }
  return null
}

/** Scroll a field up above the keyboard (in its pop-up or the page), not the pop-up up to it. */
function reveal(el: HTMLElement) {
  const vv = window.visualViewport
  if (!vv) return
  const bottom = vv.offsetTop + vv.height - 16
  const r = el.getBoundingClientRect()
  if (r.bottom <= bottom) return
  const by = Math.min(r.bottom - bottom, r.top - vv.offsetTop - 16)
  if (by <= 0) return
  const box = scroller(el)
  if (box) box.scrollBy({ top: by, behavior: 'smooth' })
  else window.scrollBy({ top: by, behavior: 'smooth' })
}

export function installViewportTracking() {
  const vv = window.visualViewport
  if (!vv) return
  vv.addEventListener('resize', update)
  vv.addEventListener('scroll', update)
  update()
}

/** The keyboard's height now (0 when it's down), and a call whenever it changes. */
export const keyboardHeight = () => kb
export function onKeyboard(f: () => void): () => void {
  subs.add(f)
  return () => subs.delete(f)
}

export function useKeyboardOpen(): boolean {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb)
      return () => subs.delete(cb)
    },
    () => kb > 0,
  )
}
